import {
  createDefaultHeroCarouselItem,
  createDefaultOffer,
} from './defaults';
import { createHeroCarouselItemDraftLabel, createOfferDraftLabel } from './offers';
import { fieldLabel } from './fieldLabels';
import type {
  OffersHubHeroCarouselItem,
  OffersHubOffer,
  OffersHubOfferTag,
  OffersHubPayloadBuilderState,
  OffersHubUrgencyType,
} from './types';
import { toDateTimeLocal } from '../dateTime';
import { OFFERS_HUB_OFFER_TAGS, OFFERS_HUB_URGENCY_TYPES } from './types';
import {
  asString,
  parseObject,
  parseKnownValue,
  parseOptionalStringArrayField,
  parseRequiredArrayField,
} from '../parseHelpers';

function parseHeroCarouselItem(value: unknown, index: number): OffersHubHeroCarouselItem {
  const label = `Hero item ${index + 1}`;
  const source = parseObject(value, label);
  const defaults = createDefaultHeroCarouselItem(createHeroCarouselItemDraftLabel(index));

  return {
    ...defaults,
    isActive: source.isActive === undefined ? defaults.isActive : source.isActive === true,
    // Preserve invalid region strings so validation can report config mistakes instead of dropping them on load.
    eligibleRegions: parseOptionalStringArrayField(source, 'eligibleRegions', `${label} eligibleRegions`),
    startAt: toDateTimeLocal(source.startAt),
    endAt: toDateTimeLocal(source.endAt),
    label: asString(source['label-i18n']),
    imageId: asString(source.imageId),
    logoImageId: asString(source.logoImageId),
    title: asString(source['title-i18n']),
    description: asString(source['description-i18n']),
    ctaLabel: asString(source['ctaLabel-i18n']),
    ctaUrl: asString(source.ctaUrl),
  };
}

function parseOffer(value: unknown, index: number): OffersHubOffer {
  const label = `Offer ${index + 1}`;
  const source = parseObject(value, label);
  const offerTag = parseKnownValue<OffersHubOfferTag>(
    source.offerTag,
    OFFERS_HUB_OFFER_TAGS,
    `${label} ${fieldLabel('offerTag')}`,
  );
  const defaults = createDefaultOffer(offerTag, createOfferDraftLabel(index));

  return {
    ...defaults,
    isActive: source.isActive === undefined ? defaults.isActive : source.isActive === true,
    // Preserve invalid region strings so validation can report config mistakes instead of dropping them on load.
    eligibleRegions: parseOptionalStringArrayField(source, 'eligibleRegions', `${label} eligibleRegions`),
    startAt: toDateTimeLocal(source.startAt),
    endAt: toDateTimeLocal(source.endAt),
    offerTag,
    brandName: asString(source.brandName),
    industry: asString(source['industry-i18n'], asString(source.industry)),
    imageId: asString(source.imageId),
    title: asString(source['title-i18n']),
    description: asString(source['description-i18n']),
    ctaLabel: asString(source['ctaLabel-i18n']),
    ctaUrl: asString(source.ctaUrl),
    urgencyType: parseKnownValue<OffersHubUrgencyType>(
      source.urgencyType,
      OFFERS_HUB_URGENCY_TYPES,
      `${label} ${fieldLabel('urgencyType')}`,
    ),
  };
}

export function parseOffersHubPayload(value: unknown): OffersHubPayloadBuilderState {
  const source = parseObject(value, 'Payload');
  const heroCarouselItems = parseRequiredArrayField(source, 'heroCarouselItems').map((item, index) =>
    parseHeroCarouselItem(item, index),
  );
  const offers = parseRequiredArrayField(source, 'offers').map((offer, index) => parseOffer(offer, index));

  return {
    mode: 'offersHub',
    heroCarouselItems,
    offers,
  };
}
